import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';

function UserManagement() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { isAdmin, currentUser, getAuthToken } = useAuth();

    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [actionStatus, setActionStatus] = useState({});
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        if (!isAdmin) {
            navigate('/');
            return;
        }
        fetchUsers();
    }, [isAdmin, navigate]);

    async function fetchUsers() {
        try {
            setIsLoading(true);
            const token = await getAuthToken();
            const response = await fetch('/api/users', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (!response.ok) {
                throw new Error('Failed to fetch users');
            }
            const data = await response.json();
            setUsers(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }

    const filteredUsers = useMemo(() => {
        if (!searchQuery.trim()) return users;
        const query = searchQuery.toLowerCase();
        return users.filter(user =>
            user.email?.toLowerCase().includes(query)
        );
    }, [users, searchQuery]);

    async function handleRoleChange(user, newRole) {
        if (user.role === newRole) return;

        try {
            setActionStatus(prev => ({ ...prev, [user.uid]: { loading: true } }));
            const token = await getAuthToken();

            const response = await fetch(`/api/users/${user.uid}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ role: newRole })
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Failed to update role');
            }

            setActionStatus(prev => ({ ...prev, [user.uid]: { success: true } }));
            setSuccessMessage(t('admin_role_updated'));

            // Clear success message after 3 seconds
            setTimeout(() => setSuccessMessage(''), 3000);

            // Refresh users
            fetchUsers();
        } catch (err) {
            setActionStatus(prev => ({ ...prev, [user.uid]: { error: err.message } }));
        }
    }

    function handleDeleteClick(user) {
        setUserToDelete(user);
        setShowDeleteModal(true);
    }

    async function confirmDeleteUser() {
        if (!userToDelete) return;

        try {
            setActionStatus(prev => ({ ...prev, [userToDelete.uid]: { loading: true } }));
            const token = await getAuthToken();

            const response = await fetch(`/api/users/${userToDelete.uid}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Failed to delete user');
            }

            setActionStatus(prev => ({ ...prev, [userToDelete.uid]: { deleted: true } }));
            setSuccessMessage(t('admin_user_deleted'));
            setShowDeleteModal(false);
            setUserToDelete(null);

            // Clear success message after 3 seconds
            setTimeout(() => setSuccessMessage(''), 3000);

            // Refresh users
            fetchUsers();
        } catch (err) {
            setActionStatus(prev => ({ ...prev, [userToDelete.uid]: { error: err.message } }));
        }
    }

    function formatDate(timestamp) {
        if (!timestamp) return t('account_unknown');

        // Handle Firestore timestamp
        const date = timestamp.seconds
            ? new Date(timestamp.seconds * 1000)
            : new Date(timestamp);

        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    }

    if (!isAdmin) {
        return (
            <div className="page-section">
                <div className="error-container">
                    <div className="error-icon">🔒</div>
                    <p className="error-message">{t('auth_access_denied')}</p>
                    <Link to="/" className="btn-primary-modern">{t('home')}</Link>
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
                <p className="loading-text">{t('loading')}</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="page-section">
                <div className="error-container">
                    <div className="error-icon">⚠️</div>
                    <p className="error-message">{t('error')}: {error}</p>
                    <Link to="/" className="btn-primary-modern">{t('home')}</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="page-section">
            {/* Header */}
            <div className="section-header">
                <div>
                    <h2 className="section-title">👥 {t('admin_user_management')}</h2>
                    <p className="section-subtitle">{t('admin_user_management_subtitle')}</p>
                </div>
            </div>

            {/* Success Message */}
            {successMessage && (
                <div className="success-banner animate-fade-in">
                    ✓ {successMessage}
                </div>
            )}

            {/* Search Bar */}
            <div className="search-bar-container" style={{ marginBottom: '1.5rem' }}>
                <div className="search-input-wrapper">
                    <span className="search-icon">🔍</span>
                    <input
                        type="text"
                        className="search-input"
                        placeholder={t('admin_search_users')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    {searchQuery && (
                        <button
                            className="search-clear-btn"
                            onClick={() => setSearchQuery('')}
                            aria-label="Clear search"
                        >
                            ✕
                        </button>
                    )}
                </div>
            </div>

            {/* Users Table */}
            <div className="admin-card">
                {filteredUsers.length === 0 ? (
                    <div className="empty-state">
                        <p>{t('admin_no_users')}</p>
                    </div>
                ) : (
                    <div className="users-table-container">
                        <table className="users-table">
                            <thead>
                                <tr>
                                    <th>{t('admin_user_email')}</th>
                                    <th>{t('admin_user_role')}</th>
                                    <th>{t('admin_user_created')}</th>
                                    <th>{t('account_actions_title')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUsers.map(user => {
                                    const status = actionStatus[user.uid];
                                    const isCurrentUser = currentUser?.uid === user.uid;

                                    return (
                                        <tr key={user.uid} className={status?.deleted ? 'row-deleted' : ''}>
                                            <td className="user-email-cell">
                                                {user.email}
                                                {isCurrentUser && <span className="you-badge">(You)</span>}
                                            </td>
                                            <td>
                                                <span className={`role-badge ${user.role}`}>
                                                    {user.role === 'admin' ? '👑 ' : user.role === 'leader' ? '🌟 ' : '👤 '}
                                                    {user.role === 'admin' ? t('account_role_admin') : user.role === 'leader' ? t('account_role_leader') : t('account_role_user')}
                                                </span>
                                            </td>
                                            <td>{formatDate(user.created_at)}</td>
                                            <td className="actions-cell">
                                                {status?.loading ? (
                                                    <span className="loading-text">{t('loading')}</span>
                                                ) : status?.error ? (
                                                    <span className="error-text">{status.error}</span>
                                                ) : (
                                                    <div className="action-buttons">
                                                        {!isCurrentUser && (
                                                            <>
                                                                <select
                                                                    className="form-select-small"
                                                                    value={user.role || 'user'}
                                                                    onChange={(e) => handleRoleChange(user, e.target.value)}
                                                                    disabled={status?.loading}
                                                                    style={{ padding: '0.25rem', borderRadius: '4px', border: '1px solid #ccc', marginRight: '0.5rem' }}
                                                                >
                                                                    <option value="user">{t('account_role_user')}</option>
                                                                    <option value="leader">{t('account_role_leader')}</option>
                                                                    <option value="admin">{t('account_role_admin')}</option>
                                                                </select>
                                                                <button
                                                                    className="btn-small btn-danger"
                                                                    onClick={() => handleDeleteClick(user)}
                                                                    title={t('admin_delete_user')}
                                                                >
                                                                    {t('delete')}
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteModal && userToDelete && (
                <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
                    <div className="modal-content modal-confirm" onClick={(e) => e.stopPropagation()}>
                        <h3 className="modal-title">⚠️ {t('admin_delete_user_confirm_title')}</h3>
                        <p className="modal-message">
                            {t('admin_delete_user_confirm_message')}
                        </p>
                        <p className="modal-detail">
                            <strong>{userToDelete.email}</strong>
                        </p>
                        <div className="modal-actions">
                            <button
                                className="btn-outline-modern"
                                onClick={() => {
                                    setShowDeleteModal(false);
                                    setUserToDelete(null);
                                }}
                            >
                                {t('cancel')}
                            </button>
                            <button
                                className="btn-danger-modern"
                                onClick={confirmDeleteUser}
                            >
                                {t('admin_delete_user_confirm_btn')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default UserManagement;
